import React, { useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Swal from "sweetalert2";
import axios from "axios";

const ComplimentaryDialog = ({
  open,
  onClose,
  posId,
  cartItems,
  employeeName,
  onSuccess,
  endpointPath = "/v1/api/order/complimentary-meal",
}) => {
  const [submitting, setSubmitting] = useState(false);

  const menuItemsPayload = useMemo(() => {
    return (cartItems || []).map((i) => ({
      menuItemId: i.menuItemId || i._id,
      name: i.menuName || "",
      quantity: i.quantity || 1,
      options: i.selectedOptions || [],
    }));
  }, [cartItems]);  

  const handleClaim = async () => {
    const showSwal = async (config) => {
      onClose?.();                
      return Swal.fire(config);    
    };
  
    if (!employeeName?.trim()) {
      await showSwal({
        icon: "error",
        title: "Employee name required",
        text: "Please enter employee name.",
      });
      return;
    }
    if (!posId) {
      await showSwal({ icon: "error", title: "POS error", text: "Missing POS ID." });
      return;
    }
    if (!menuItemsPayload.length) {
      await showSwal({
        icon: "error",
        title: "No items",
        text: "Your cart is empty.",
      });
      return;
    }
  
    try {
      setSubmitting(true);
  
      const url = `${process.env.REACT_APP_BACKEND_URL}${endpointPath}`;
      const body = {
        posId,
        menuItems: menuItemsPayload,
        salesType: "Dine-In",
        voucherCode: "",
        tenderType: "Cash",
        tentNumber: "FMeal",
        name: employeeName,
      };
  
      const resp = await axios.post(url, body, { responseType: "blob" });
  
      const contentType = resp.headers["content-type"] || "";
      if (contentType.includes("application/pdf") || contentType.startsWith("application/")) {
        const blob = new Blob([resp.data], { type: contentType });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `complimentary-receipt-${Date.now()}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
  
      await showSwal({
        icon: "success",
        title: "Complimentary Meal Claimed",
        text: `Claimed by ${employeeName}. Enjoy your meal!`,
        timer: 1500,
        showConfirmButton: false,
      });
  
      onSuccess?.();
  
      const parts = window.location.pathname.split("/").filter(Boolean);
      const base = parts[0] ? `/${parts[0]}` : "/"; 
      window.location.replace(base); 
    } catch (err) {
      let serverMsg = "Server error.";
      try {
        const text = await err?.response?.data?.text?.();
        const json = JSON.parse(text || "{}");
        serverMsg = json?.message || serverMsg;
      } catch {
        serverMsg = err?.response?.data?.message || err?.message || serverMsg;
      }
  
      await showSwal({ icon: "error", title: "Unable to claim", text: serverMsg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Confirm Complimentary Meal</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Please confirm the employee’s name and the items to be claimed.
        </Typography>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Employee: <b>{employeeName || "(not set)"}</b>
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Items to claim:
        </Typography>
        <List dense>
          {(cartItems || []).map((i) => (
            <ListItem key={i.id} alignItems="flex-start" sx={{ px: 0 }}>
              <ListItemText
                primary={`${i.menuName} × ${i.quantity}`}
                secondary={
                  Array.isArray(i.selectedOptions) && i.selectedOptions.length
                    ? i.selectedOptions
                        .map((opt) => {
                          if (Array.isArray(opt.choices)) {
                            return `${opt.name || "Option"}: ${opt.choices
                              .map((c) =>
                                typeof c === "object" ? c.name : String(c)
                              )
                              .join(", ")}`;
                          }
                          const c =
                            typeof opt.choice === "object"
                              ? opt.choice?.name
                              : opt.choice;
                          return `${opt.name || "Option"}: ${c || ""}`;
                        })
                        .join(" • ")
                    : null
                }
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={submitting} variant="text">
          Cancel
        </Button>
        <Button
          onClick={handleClaim}
          variant="contained"
          color="primary"
          disabled={submitting}
          startIcon={
            submitting ? (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <CircularProgress size={18} sx={{ mr: 1 }} />
              </Box>
            ) : null
          }
        >
          {submitting ? "Claiming..." : "Claim"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ComplimentaryDialog;
